import baseApi from '@/modules/services/base';
import {
  LiveAssessmentTrackingType,
  QuizAttemptQueryType,
  QuizAttemptResponseType,
  QuizAttemptStartResponseType,
  QuizAttemptStartType,
  QuizDataType,
} from '@/modules/types/quiz';
import { QuizzesTag, QuizAttemptTag, MyElearningDetailsTag } from './tags';
import { getContentTypeResponse } from '@/lib/util';

const quizzesApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getQuizzes: build.query<QuizDataType[], void>({
      query: () => `quizzes`,
      providesTags: [QuizzesTag],
    }),
    getQuiz: build.query<QuizDataType, string>({
      query: (data) => `quizzes/${data}`,
      providesTags: [QuizzesTag],
    }),
    createQuiz: build.mutation<QuizDataType, QuizDataType>({
      query: (post) => ({
        url: `quizzes`,
        method: 'POST',
        body: post,
        responseType: 'json',
      }),
      invalidatesTags: [QuizzesTag],
    }),
    updateQuiz: build.mutation<QuizDataType, QuizDataType>({
      query: (post) => ({
        url: `quizzes/${post.id}`,
        method: 'PUT',
        body: post,
        responseType: 'json',
      }),
      invalidatesTags: [QuizzesTag, MyElearningDetailsTag],
    }),
    postQuizQuestions: build.mutation<QuizDataType, string>({
      query: (id) => ({
        url: `quizzes/${id}/questions`,
        method: 'POST',
        responseType: 'json',
      }),
    }),
    getFirstRandomQuestion: build.mutation<
      QuizDataType,
      { quizId: string | undefined }
    >({
      query: (data) => ({
        url: `quizzes/${data.quizId}/questions`,
        method: 'POST',
      }),
    }),
    submitQuizResult: build.mutation<
      QuizDataType,
      { quizId: string | undefined }
    >({
      query: (data) => ({
        url: `quizzes/${data.quizId}/results`,
        method: 'POST',
      }),
    }),
    submitQuizResultLive: build.mutation<string, LiveAssessmentTrackingType>({
      query: (data) => ({
        url: `live-assessment-tracking/${data.assessmentTrackingId}/submit-result/`,
        method: 'POST',
        body: data,
        responseHandler: getContentTypeResponse,
      }),
    }),
    getLiveQuizAttempt: build.query<
      QuizAttemptResponseType,
      QuizAttemptQueryType
    >({
      query: (data) =>
        `live-assessment-tracking/module/${data.moduleId}/course/${data.courseId}/assessment-attempts?attemptLimit=${data.attemptLimit}`,
      providesTags: [QuizAttemptTag],
    }),
    startLiveQuizAttempt: build.mutation<
      QuizAttemptStartResponseType,
      QuizAttemptStartType
    >({
      query: (data) => ({
        url: `live-assessment-tracking/start-assessment`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [QuizAttemptTag],
    }),
    evaluateLiveQuizAttempt: build.mutation<
      QuizAttemptStartResponseType,
      QuizAttemptStartType
    >({
      query: (data) => ({
        url: `live-assessment-tracking/evaluate-assessment`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [QuizAttemptTag],
    }),
  }),
  overrideExisting: false,
});

export const {
  useSubmitQuizResultMutation,
  useGetFirstRandomQuestionMutation,
  usePostQuizQuestionsMutation,
  useUpdateQuizMutation,
  useCreateQuizMutation,
  useGetQuizQuery,
  useGetQuizzesQuery,
  useSubmitQuizResultLiveMutation,
  useGetLiveQuizAttemptQuery,
  useStartLiveQuizAttemptMutation,
  useEvaluateLiveQuizAttemptMutation,
} = quizzesApi;

export default quizzesApi;
